import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';

const RARITIES = ['common', 'rare', 'epic', 'unique', 'legendary'];

export default function CollectionModal({ open, onClose }) {
  const [collection, setCollection] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { walletAddress } = useCoin();

  // 새로 추가: 클릭된 카드 상태
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    if (!open) return;

    const fetchCollection = async () => {
      setLoading(true);
      setError(null);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const headers = {};
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }

        const response = await supabase.functions.invoke('get-collection', {
          headers: headers,
        });

        if (response.error) {
          console.error(response.error);
          throw new Error(
            response.error.message || 'Failed to fetch collection'
          );
        }

        if (response.data && response.data.collection) {
          console.log('collection from server:', response.data.collection);
          setCollection(response.data.collection);
        } else {
          throw new Error('Invalid data format');
        }
      } catch (err) {
        console.error('Collection error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [open, walletAddress]);

  if (!open) return null;

  // 공통 카드 렌더러 (한 줄에 5장 들어가도록 사이즈 조정)
  const renderCardItem = (card) => (
    <div
      key={card.card_index}
      style={{
        background: 'rgba(255,255,255,0.9)',
        borderRadius: 12,
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        opacity: card.is_owned ? 1 : 0.6,
        position: 'relative',
        filter: card.is_owned ? 'none' : 'grayscale(100%)',
        flex: 1,
        minWidth: 0,
        maxWidth: 70,
        cursor: 'pointer',
      }}
      onClick={() => {
        if (card.is_owned) {
          setSelectedCard(card);
        }
      }}
    >
      {/* Quantity Badge */}
      {card.is_owned && card.quantity > 1 && (
        <div
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            background: '#EF4444',
            color: 'white',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 11,
            fontWeight: 'bold',
            zIndex: 2,
          }}
        >
          {card.quantity}
        </div>
      )}

      <div
        style={{
          width: '100%',
          aspectRatio: '9/14',
          background: '#ccc',
          borderRadius: 6,
          marginBottom: 4,
          overflow: 'hidden',
        }}
      >
        <img
          src={card.card_image_url}
          alt={card.card_name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        {card.card_name}
      </div>
      <div style={{ fontSize: 9, color: '#666' }}>{card.card_grade}</div>
    </div>
  );

  // 특정 이름 + 모든 레어리티를 원하는 순서로 나열
  const renderThemeRow = (title, baseName) => {
    const cardsByRarity = RARITIES.map((rarity) => {
      return collection.find(
        (c) =>
          String(c.card_name || '').toLowerCase() === baseName.toLowerCase() &&
          typeof c.card_grade === 'string' &&
          c.card_grade.toLowerCase() === rarity
      );
    }).filter(Boolean);

    return (
      <div
        key={title}
        style={{
          marginBottom: 20,
        }}
      >
        <div
          style={{
            color: '#F9FAFB',
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8,
            textShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            justifyContent: 'flex-start',
          }}
        >
          {cardsByRarity.length === 0 ? (
            <div
              style={{
                color: '#E5E7EB',
                fontSize: 12,
                opacity: 0.8,
                padding: '8px 0',
              }}
            >
              No cards in this theme.
            </div>
          ) : (
            cardsByRarity.map((card) => renderCardItem(card))
          )}
        </div>
      </div>
    );
  };

  // Carry Duo: Jeth / Mango 각각 가장 높은 레어리티 1장씩
  const getHighestRarityCard = (baseName) => {
    const owned = collection.filter(
      (c) =>
        c.is_owned &&
        String(c.card_name || '').toLowerCase() === baseName.toLowerCase()
    );
    if (owned.length === 0) return null;

    let best = null;
    let bestIdx = -1;
    owned.forEach((c) => {
      const idx = RARITIES.indexOf(String(c.card_grade || '').toLowerCase());
      if (idx > bestIdx) {
        bestIdx = idx;
        best = c;
      }
    });
    return best;
  };

  // 가장 낮은 레어리티 카드 (보유 여부 상관 없음)
  const getLowestRarityCard = (baseName) => {
    const all = collection.filter(
      (c) =>
        String(c.card_name || '').toLowerCase() === baseName.toLowerCase()
    );
    if (all.length === 0) return null;

    let worst = null;
    let worstIdx = Infinity;
    all.forEach((c) => {
      const idx = RARITIES.indexOf(String(c.card_grade || '').toLowerCase());
      if (idx !== -1 && idx < worstIdx) {
        worstIdx = idx;
        worst = c;
      }
    });
    return worst;
  };

  // Carry Duo용 카드 선택 로직:
  // 1순위: 보유한 카드 중 최고 레어도
  // 2순위: 아무것도 없으면 가장 낮은 레어도 카드 하나를 is_owned: false 로 만들어서 표시용으로 사용
  const carryJethOwned = getHighestRarityCard('jeth');
  const carryMangoOwned = getHighestRarityCard('mango');

  const carryJeth =
    carryJethOwned ||
    (() => {
      const base = getLowestRarityCard('jeth');
      return base ? { ...base, is_owned: false, quantity: 0 } : null;
    })();

  const carryMango =
    carryMangoOwned ||
    (() => {
      const base = getLowestRarityCard('mango');
      return base ? { ...base, is_owned: false, quantity: 0 } : null;
    })();

  const renderCarryDuoRow = () => {
    const duoCards = [carryJeth, carryMango].filter(Boolean);
    return (
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div
          style={{
            color: '#F9FAFB',
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8,
            textShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}
        >
          Carry Duo
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            justifyContent: 'flex-start',
          }}
        >
          {duoCards.length === 0 ? (
            <div
              style={{
                color: '#E5E7EB',
                fontSize: 12,
                opacity: 0.8,
                padding: '8px 0',
              }}
            >
              No eligible cards for Carry Duo.
            </div>
          ) : (
            duoCards.map((card) => renderCardItem(card))
          )}
        </div>
      </div>
    );
  };

  // 큰 카드 모달 컴포넌트
  const FullCardModal = ({ card, onClose }) => {
    if (!card) return null;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: 'linear-gradient(180deg, #111827 0%, #1F2937 100%)',
            borderRadius: 24,
            padding: 16,
            boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
            maxWidth: '80vw',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#F9FAFB',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>

          <div
            style={{
              width: 'min(260px, 70vw)',
              aspectRatio: '9/14',
              borderRadius: 16,
              overflow: 'hidden',
              background: '#4B5563',
              marginBottom: 12,
            }}
          >
            <img
              src={card.card_image_url}
              alt={card.card_name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          <div
            style={{
              color: '#F9FAFB',
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 4,
              textAlign: 'center',
            }}
          >
            {card.card_name}
          </div>
          <div
            style={{
              color: '#E5E7EB',
              fontSize: 14,
              textTransform: 'capitalize',
            }}
          >
            {card.card_grade}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: '90%',
            maxWidth: 420,
            maxHeight: '85vh',
            background: 'linear-gradient(180deg, #A78BFA 0%, #7C3AED 100%)',
            borderRadius: 24,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            position: 'relative',
            padding: '32px 20px 20px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'rgba(255,255,255,0.3)',
              fontSize: 20,
              position: 'absolute',
              top: 16,
              right: 16,
              cursor: 'pointer',
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 'bold',
              zIndex: 10,
            }}
          >
            ✕
          </button>

          <h2
            style={{
              margin: '0 0 24px 0',
              textAlign: 'center',
              color: '#fff',
              fontSize: 28,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            • Collection •
          </h2>

          <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
            {loading ? (
              <div
                style={{
                  textAlign: 'center',
                  color: '#fff',
                  marginTop: 40,
                }}
              >
                Loading...
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: 'center',
                  color: '#fff',
                  marginTop: 40,
                }}
              >
                Error: {error}
              </div>
            ) : (
              <>
                {renderThemeRow('Jeth', 'jeth')}
                {renderThemeRow('Mango', 'mango')}
                {renderCarryDuoRow()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 큰 카드 모달 */}
      <FullCardModal card={selectedCard} onClose={() => setSelectedCard(null)} />
    </>
  );
}
